import posthog from 'posthog-js'
import { useEffect, useState } from 'react'

import { FeatureFlag } from '@mangovoice/constants'

import { onPosthogReady, onPosthogSkipped } from './posthogReady'

export interface FeatureFlagState {
  /** True once flags arrive, or PostHog is skipped (no region key). */
  isReady: boolean
  /** False until flags load. Off when skipped. */
  isEnabled: boolean
  /** Variant key for multivariate flags, true/false for boolean flags. */
  variant: boolean | string | undefined
  /**
   * True when the latest flag load reported errors (request failed or timed
   * out, or the server couldn't compute every flag), so `isEnabled: false` may
   * mean "unknown" rather than "off".
   */
  loadFailed: boolean
}

/**
 * posthog-js records the latest load's errors here (`[]` after a clean load).
 * Read it as well as the callback context: a subscriber that joins after a
 * load gets the flags replayed with no context argument at all.
 */
const FLAG_ERRORS_PROPERTY = '$feature_flag_errors'

const latestLoadFailed = (context?: { errorsLoading?: boolean }) => {
  const errors: unknown = posthog.get_property(FLAG_ERRORS_PROPERTY)
  return (
    context?.errorsLoading === true ||
    (Array.isArray(errors) && errors.length > 0)
  )
}

const FLAGS_NOT_READY: FeatureFlagState = {
  isReady: false,
  isEnabled: false,
  variant: undefined,
  loadFailed: false,
}

const FLAGS_SKIPPED: FeatureFlagState = {
  isReady: true,
  isEnabled: false,
  variant: undefined,
  loadFailed: false,
}

/**
 * Dev-only flag overrides: keys forced on locally before they exist in PostHog.
 *
 * Core can't read `import.meta.env` (it's built by each app, not Vite — same
 * reason getPosthogConfig takes keys as args), so the app registers the set via
 * setFeatureFlagOverrides. Callers should only ever populate it under
 * import.meta.env.DEV, so it dead-code-eliminates out of prod builds.
 */
let flagOverrides: ReadonlySet<string> = new Set<string>()

export const setFeatureFlagOverrides = (flags: Iterable<string>): void => {
  flagOverrides = new Set(flags)
}

const FLAG_FORCED_ON: FeatureFlagState = {
  isReady: true,
  isEnabled: true,
  variant: true,
  loadFailed: false,
}

/**
 * Reads a PostHog feature flag, re-rendering when flags arrive or change.
 * Stays not-ready until PostHog initializes (then waits for flags) or is
 * skipped (no key for the region). Skip resolves as ready + disabled so
 * fallback paths still fetch. Pre-login (nobody signed in yet) stays not-ready.
 */
export const useFeatureFlag = (flag: FeatureFlag): FeatureFlagState => {
  const forcedOn = flagOverrides.has(flag)
  const [state, setState] = useState<FeatureFlagState>(FLAGS_NOT_READY)

  useEffect(() => {
    // Dev override wins and needs no PostHog. Skipped init (no region key)
    // otherwise resolves as ready + disabled, so overrides are how you force a
    // flag on locally.
    if (forcedOn) {
      setState(FLAG_FORCED_ON)
      return
    }

    setState(FLAGS_NOT_READY)

    let unsubscribeFlags: (() => void) | undefined
    const unsubscribeReady = onPosthogReady(() => {
      unsubscribeFlags = posthog.onFeatureFlags(
        (_flags, _variants, context) => {
          setState({
            isReady: true,
            isEnabled: posthog.isFeatureEnabled(flag) === true,
            variant: posthog.getFeatureFlag(flag),
            loadFailed: latestLoadFailed(context),
          })
        },
      )
    })
    const unsubscribeSkipped = onPosthogSkipped(() => {
      setState(FLAGS_SKIPPED)
    })

    return () => {
      unsubscribeReady()
      unsubscribeSkipped()
      unsubscribeFlags?.()
    }
  }, [flag, forcedOn])

  return state
}
